import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Row, 
  Col,
  Pagination,
  InputGroup
} from 'react-bootstrap';
import { tableAPI, recordAPI, permissionsAPI, authAPI, mgbSetupAPI } from '../api/api';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiSettings, FiArrowUp, FiArrowDown, FiDownload, FiEye } from 'react-icons/fi';
import FieldConfigPanel from './FieldConfigPanel';
import RecordDetailModal from './RecordDetailModal';
import { TableSkeleton } from './SkeletonLoader';
import { InfoTooltip } from './Tooltips';
import { exportToXLSX, exportFilteredRecords } from '../utils/exportUtils';
import './TableViewer.css';

// System tables that cannot be edited through the standard table editor
const PROTECTED_SYSTEM_TABLES = [
  'MGB_ClientSettings',
  'MGB_Users',
  'MGB_Groups',
  'MGB_UserGroups',
  'MGB_TablePermissions',
  'MGB_MenuPermissions',
  'MGB_Clients',
  'MGB_Roles'
];

function TableViewer() {
  const { tableName } = useParams();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [visibleFields, setVisibleFields] = useState([]);
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeys, setForeignKeys] = useState([]);
  const [linkedFieldOptions, setLinkedFieldOptions] = useState({});
  const [fieldLinks, setFieldLinks] = useState([]);
  const [searchField, setSearchField] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');
  const [fieldFilters, setFieldFilters] = useState({});
  const [displayNames, setDisplayNames] = useState({});
  const [fieldVisibility, setFieldVisibility] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [primaryKeyFields, setPrimaryKeyFields] = useState([]);
  const [isTableRegistered, setIsTableRegistered] = useState(true);
  const [tableMetadata, setTableMetadata] = useState({
    LongViewLinkfromTable: true,
    EditViewLinkfromTable: true,
    AddnewLinkfromTable: true,
    DeletefromTableLink: true,
    AddFormatID: null,
    EditFormatID: null,
    SearchFormatID: null,
    SearchResultFormatID: null
  });
  const [formats, setFormats] = useState({
    addFormat: null,
    editFormat: null,
    searchFormat: null,
    searchResultFormat: null
  });
  // Permission state
  const [permissions, setPermissions] = useState({
    CanView: true,
    CanCreate: false,
    CanEdit: false,
    CanDelete: false,
    CanExport: false
  });
  const [isAdmin, setIsAdmin] = useState(false);
  // Cross-table (many-to-many) support
  const [crossTableConfigs, setCrossTableConfigs] = useState({});
  const [crossTableAvailableItems, setCrossTableAvailableItems] = useState({});
  const [crossTableSelectedItems, setCrossTableSelectedItems] = useState({});

  // Log when component mounts or tableName changes
  useEffect(() => {
    if (!tableName) {

      setError('No table specified');
      setLoading(false);
      return;
    }
  }, [tableName]);

  // Debounced field filters for text inputs only
  const [debouncedFieldFilters, setDebouncedFieldFilters] = useState({});
  const [immediateFieldFilters, setImmediateFieldFilters] = useState({});
  
  // Separate immediate filters (from dropdowns) and debounced filters (from text inputs)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFieldFilters(fieldFilters);
    }, 500);
    return () => clearTimeout(timer);
  }, [fieldFilters]);

  // Combine immediate and debounced filters
  const combinedFilters = useMemo(() => {
    return { ...immediateFieldFilters, ...debouncedFieldFilters };
  }, [immediateFieldFilters, debouncedFieldFilters]);

  // Fetch table structure and records
  useEffect(() => {
    if (!tableName) {
      return;
    }
    fetchTableStructure();
    fetchRecords();
    fetchPermissions();
  }, [tableName, currentPage, pageSize, searchQuery, searchField, sortBy, sortOrder, keywordSearch, immediateFieldFilters, debouncedFieldFilters]);

  // Handle MGB_TablePermissions TableName conversion when foreignKeyData becomes available
  useEffect(() => {
    if (tableName === 'MGB_TablePermissions' && showModal && modalMode === 'edit' && formData.TableName) {
      // Ensure TableName is in display format, not ID format
      const currentValue = String(formData.TableName);
      
      // Check if we have the foreignKeyData available
      if (foreignKeyData['TableName'] && Array.isArray(foreignKeyData['TableName'])) {
        // If current value is numeric, it's an ID - convert to display
        if (!isNaN(currentValue)) {
          const matchedTable = foreignKeyData['TableName'].find(
            item => String(item.id) === currentValue
          );
          if (matchedTable) {
            setFormData(prev => ({
              ...prev,
              TableName: matchedTable.display
            }));
          }
        }
      }
    }
  }, [foreignKeyData, showModal, modalMode, formData.TableName, tableName]);

  const fetchTableStructure = async () => {
    try {
      
      // Fetch table metadata from MGB Setup API to get CRUD permissions
      try {
        const mgbRes = await tableAPI.getMGBTableMetadata(tableName);
        if (mgbRes.data && mgbRes.data.success && mgbRes.data.table) {
          const table = mgbRes.data.table;
          setIsTableRegistered(true);
          
          // Handle format IDs - they might be null, undefined, or numbers
          const addFormatID = table.AddFormatID != null ? Number(table.AddFormatID) : null;
          const editFormatID = table.EditFormatID != null ? Number(table.EditFormatID) : null;
          const searchFormatID = table.SearchFormatID != null ? Number(table.SearchFormatID) : null;
          const searchResultFormatID = table.SearchResultFormatID != null ? Number(table.SearchResultFormatID) : null;
          
          setTableMetadata({
            LongViewLinkfromTable: table.LongViewLinkfromTable === 1 || table.LongViewLinkfromTable === true,
            EditViewLinkfromTable: table.EditViewLinkfromTable === 1 || table.EditViewLinkfromTable === true,
            AddnewLinkfromTable: table.AddnewLinkfromTable === 1 || table.AddnewLinkfromTable === true,
            DeletefromTableLink: table.DeletefromTableLink === 1 || table.DeletefromTableLink === true,
            AddFormatID: addFormatID,
            EditFormatID: editFormatID,
            SearchFormatID: searchFormatID,
            SearchResultFormatID: searchResultFormatID
          });
          
          // Fetch format configurations if format IDs are set
          const formatIds = {
            add: addFormatID,
            edit: editFormatID,
            search: searchFormatID,
            searchResult: searchResultFormatID
          };
          
          // Fetch all formats and filter by IDs
          if (formatIds.add || formatIds.edit || formatIds.search || formatIds.searchResult) {
            try {
              const formatsRes = await mgbSetupAPI.getFormats();
              
              if (formatsRes.data && formatsRes.data.success && formatsRes.data.formats) {
                const allFormats = formatsRes.data.formats;
                
                // Convert format IDs to numbers for comparison
                const addFormat = formatIds.add ? allFormats.find(f => Number(f.FormatID) === Number(formatIds.add)) : null;
                const editFormat = formatIds.edit ? allFormats.find(f => Number(f.FormatID) === Number(formatIds.edit)) : null;
                const searchFormat = formatIds.search ? allFormats.find(f => Number(f.FormatID) === Number(formatIds.search)) : null;
                const searchResultFormat = formatIds.searchResult ? allFormats.find(f => Number(f.FormatID) === Number(formatIds.searchResult)) : null;
                
                setFormats({
                  addFormat,
                  editFormat,
                  searchFormat,
                  searchResultFormat
                });
              }
            } catch (formatErr) {
              // Format loading failed, continue without formats
            }
          }
        } else {
          setIsTableRegistered(false);
        }
      } catch (metaErr) {
        setIsTableRegistered(false);
        // Use defaults (all true) if metadata cannot be fetched
      }
      
      const fieldsRes = await tableAPI.getTableFields(tableName);
      const fkRes = await tableAPI.getForeignKeys(tableName);
      
      if (fieldsRes.data.success) {
        setFields(fieldsRes.data.fields);
        
        // Get actual primary key columns from database (works for IDENTITY and composite keys)
        try {
          const pkRes = await tableAPI.getPrimaryKey(tableName);
          if (pkRes.data.success && pkRes.data.primaryKeyColumns) {
            const pkFields = pkRes.data.primaryKeyColumns.map(pk => pk.fieldName);
            setPrimaryKeyFields(pkFields);
          }
        } catch (pkErr) {

          // Fallback: Identify primary key fields with IDENTITY
          const pkFields = fieldsRes.data.fields
            .filter(f => f.defaultValue && f.defaultValue.includes('identity'))
            .map(f => f.fieldName);
          setPrimaryKeyFields(pkFields);
        }
        
        const displayNamesMap = {};
        fieldsRes.data.fields.forEach(field => {
          displayNamesMap[field.fieldName] = field.displayName || field.fieldName;
        });
        setDisplayNames(displayNamesMap);
      }
      
      // Field metadata is now embedded in the fields response
      // Each field has: addable, editable, shortViewable, searchable
      // Filter visible fields based on shortViewable property (1 or true = visible)
      const visible = fieldsRes.data.fields.filter(f => {
        const isVisible = f.shortViewable === 1 || f.shortViewable === true;
        const isPassword = f.type === 'password';
        return isVisible && !isPassword;
      });
      setVisibleFields(visible);

      // Load field links configuration
      try {
        const linksRes = await tableAPI.getFieldLinks(tableName);
        
        // Get the fieldLinks array from the response (it's wrapped in success/fieldLinks properties)
        const links = linksRes.data.fieldLinks || linksRes.data || [];
        
        if (linksRes.data.success && links.length > 0) {
          setFieldLinks(links);
          // Fetch linked field options for each linked field
          const options = {};
          for (const link of links) {
            if (link.IsLinked === 1 || link.IsLinked === true) {
              try {
                const optRes = await tableAPI.getLinkedFieldOptions(tableName, link.LinkedTableName, link.LinkedFieldName);
                if (optRes.data && optRes.data.success && optRes.data.options && optRes.data.options.length > 0) {
                  // Store options under both original and lower-cased keys for case-insensitive lookup
                  options[link.FieldName] = optRes.data.options;
                  options[link.FieldName.toLowerCase()] = optRes.data.options;
                  continue;
                }
              } catch (optErr) {

              }

              // Fallback: attempt to fetch using primary key + foreign-key-data endpoint
              try {
                const pkRes = await tableAPI.getPrimaryKey(link.LinkedTableName);
                let pkField = null;
                if (pkRes.data && pkRes.data.success && pkRes.data.primaryKeyColumns && pkRes.data.primaryKeyColumns.length > 0) {
                  pkField = pkRes.data.primaryKeyColumns[0].fieldName;
                } else {
                  // common fallback names
                  pkField = 'ID';
                }
                const fkDataRes = await tableAPI.getForeignKeyData(tableName, link.LinkedTableName, pkField, link.LinkedFieldName);
                if (fkDataRes.data && fkDataRes.data.success && fkDataRes.data.data) {
                  // fkDataRes returns array of { id, display }
                  const fkOptions = fkDataRes.data.data.map(row => ({ id: row.id, display: row.display }));
                  options[link.FieldName] = fkOptions;
                  options[link.FieldName.toLowerCase()] = fkOptions;
                }
              } catch (fallbackErr) {

              }
            }
          }
          setLinkedFieldOptions(options);
        }
      } catch (linksErr) {

      }

      // Load cross-table (many-to-many) configurations
      try {
        const groupIDField = fieldsRes.data.fields.find(f => f.fieldName === 'groupID');
        const multiSelectFields = fieldsRes.data.fields.filter(f => 
          f.type === 'multiselect' && f.subRecordID
        );

        const configs = {};
        const availableItems = {};

        for (const field of multiSelectFields) {
          const xbyxID = field.subRecordID;
          
          try {
            // Get cross-table configuration
            const configRes = await recordAPI.getCrossTableConfig(xbyxID);
            if (configRes.data && configRes.data.success && configRes.data.config) {
              configs[field.fieldName] = configRes.data.config;

              // Get available items - pass current table name for reverse relationship detection
              const itemsRes = await recordAPI.getCrossTableAvailableItems(xbyxID, tableName);
              if (itemsRes.data && itemsRes.data.success) {
                availableItems[field.fieldName] = {
                  items: itemsRes.data.items,
                  config: itemsRes.data.config
                };
              }
            }
          } catch (ctErr) {
            // Error loading cross-table configuration
          }
        }

        setCrossTableConfigs(configs);
        setCrossTableAvailableItems(availableItems);
      } catch (ctSetupErr) {

      }

      if (fkRes.data.success) {
        setForeignKeys(fkRes.data.foreignKeys);
        // Fetch foreign key data
        fetchForeignKeyData(fkRes.data.foreignKeys);
      }
    } catch (err) {
      setError('Failed to load table structure');

    }
  };

  // Fetch user permissions for the table
  const fetchPermissions = async () => {
    try {

      
      // Get current user's session data
      const sessionRes = await authAPI.checkSession();

      
      if (sessionRes.data.success && sessionRes.data.user) {
        const user = sessionRes.data.user;
        const userRole = user.Role || user.role || '';
        const groupIds = user.groupIds || [user.groupId];
        const groupId = groupIds[0];
        
        // Check if user is admin (case-insensitive check)
        const isUserAdmin = userRole.toLowerCase() === 'admin';
        setIsAdmin(isUserAdmin);

        
        // Admin users get full permissions
        if (isUserAdmin) {

          setPermissions({
            CanView: true,
            CanCreate: true,
            CanEdit: true,
            CanDelete: true,
            CanExport: true
          });
          return;
        }
        

        
        // Call permissions API to get current permissions
        const permRes = await permissionsAPI.getGroupPermissions(groupId);

        
        if (permRes.data.success && permRes.data.tablePermissions) {
          // Find permissions for this specific table
          const tablePerms = permRes.data.tablePermissions.find(tp => tp.TableName === tableName);

          
          if (tablePerms) {
            const newPerms = {
              CanView: tablePerms.CanView === 1 || tablePerms.CanView === true,
              CanCreate: tablePerms.CanCreate === 1 || tablePerms.CanCreate === true,
              CanEdit: tablePerms.CanEdit === 1 || tablePerms.CanEdit === true,
              CanDelete: tablePerms.CanDelete === 1 || tablePerms.CanDelete === true,
              CanExport: tablePerms.CanExport === 1 || tablePerms.CanExport === true
            };

            setPermissions(newPerms);

          } else {
            // No specific permissions - set all to false
            setPermissions({
              CanView: false,
              CanCreate: false,
              CanEdit: false,
              CanDelete: false,
              CanExport: false
            });

          }
        } else {

          // Default to view-only if API doesn't return permissions
          setPermissions({
            CanView: true,
            CanCreate: false,
            CanEdit: false,
            CanDelete: false,
            CanExport: false
          });
        }
      } else {

      }
    } catch (err) {

      // On error, check if it's a 403 (unauthorized) - user may still have metadata-based permissions
      // Default: only View allowed unless error indicates otherwise
      setPermissions({
        CanView: true,
        CanCreate: false,
        CanEdit: false,
        CanDelete: false,
        CanExport: false
      });
    }
  };

  const fetchForeignKeyData = async (fks) => {
    const fkData = {};
    for (const fk of fks) {
      try {
        // Special handling for TableName field in MGB_TablePermissions
        if (tableName === 'MGB_TablePermissions' && fk.localColumn === 'TableName') {
          const response = await tableAPI.getForeignKeyData(
            'MGB_Tables',
            'TableName',
            'TableName'
          );
          if (response.data.success) {
            fkData[fk.localColumn] = response.data.data;
          }
        } else {
          const response = await tableAPI.getForeignKeyData(
            fk.referencedTable,
            fk.referencedColumn,
            fk.referencedColumn
          );
          if (response.data.success) {
            fkData[fk.localColumn] = response.data.data;
          }
        }
      } catch (err) {

      }
    }
    setForeignKeyData(fkData);
  };

  const handleColumnSort = (fieldName) => {
    if (sortBy === fieldName) {
      // Toggle sort order if same column is clicked
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column
      setSortBy(fieldName);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      // Use new search method if keywordSearch or combinedFilters are set, otherwise use legacy
      const hasNewSearch = keywordSearch || Object.keys(combinedFilters).some(key => combinedFilters[key] !== null && combinedFilters[key] !== undefined && combinedFilters[key] !== '');
      
      // Convert display values to IDs for linked/foreign key fields before sending to backend
      const processedFilters = {};
      if (hasNewSearch && combinedFilters) {
        for (const [fieldName, filterValue] of Object.entries(combinedFilters)) {
          if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
            // Check if this is a date range object (has 'from' or 'to' properties)
            if (typeof filterValue === 'object' && !Array.isArray(filterValue) && (filterValue.from || filterValue.to)) {
              // Date range filter - only include if at least one date is set
              if (filterValue.from || filterValue.to) {
                processedFilters[fieldName] = {
                  from: filterValue.from || null,
                  to: filterValue.to || null
                };
              }
            } else {
              // Check if this is a linked field
              const isLinked = fieldLinks.some(fl => 
                fl.FieldName && fl.FieldName.toLowerCase() === fieldName.toLowerCase() && 
                (fl.IsLinked === 1 || fl.IsLinked === true)
              );
              const isForeignKey = foreignKeys.some(fk => fk.localColumn === fieldName);
              
              // For text input filters on linked/foreign key fields, send the text as-is
              // The backend will handle searching in the linked table's display field
              // Only convert to ID for dropdown selections (exact matches)
              const isDropdownFilter = isLinked && linkedFieldOptions[fieldName] && 
                linkedFieldOptions[fieldName].some(item => item.display === filterValue);
              const isForeignKeyDropdown = isForeignKey && foreignKeyData[fieldName] && 
                foreignKeyData[fieldName].some(item => item.display === filterValue);
              
              if (isDropdownFilter) {
                // Dropdown selection - convert display value to ID
                const matchedOption = linkedFieldOptions[fieldName].find(item => item.display === filterValue);
                if (matchedOption) {
                  processedFilters[fieldName] = String(matchedOption.id);
                } else {
                  processedFilters[fieldName] = String(filterValue);
                }
              } else if (isForeignKeyDropdown) {
                // Foreign key dropdown selection - convert display value to ID
                const matchedOption = foreignKeyData[fieldName].find(item => item.display === filterValue);
                if (matchedOption) {
                  processedFilters[fieldName] = String(matchedOption.id);
                } else {
                  processedFilters[fieldName] = String(filterValue);
                }
              } else {
                // Text input or partial match - send as-is, backend will handle linked field search
                processedFilters[fieldName] = filterValue;
              }
            }
          }
        }
      }
      
      const response = await recordAPI.getRecords(
        tableName,
        currentPage,
        pageSize,
        hasNewSearch ? null : searchQuery,
        hasNewSearch ? null : searchField,
        sortBy,
        sortOrder,
        keywordSearch || null,
        hasNewSearch ? processedFilters : null
      );
      
      if (response.data.success) {
        setRecords(response.data.data.records);
        setTotalRecords(response.data.data.total);
      } else {
        setError('Failed to load records');

      }
    } catch (err) {
      setError('Failed to load records');

    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    navigate(`/tables/${tableName}/add`);
  };

  const handleEditClick = (record) => {
    // Get the primary key value for the record
    const recordID = record.ID || record[primaryKeyFields[0]] || Object.values(record)[0];
    navigate(`/tables/${tableName}/edit/${recordID}`);
  };

  // Render a single form field
  const renderFormField = (field) => {
    // Check if this field is linked (case-insensitive comparison)
    const linkedField = fieldLinks.find(fl => 
      fl.FieldName && fl.FieldName.toLowerCase() === field.fieldName.toLowerCase()
    );
    const isLinked = linkedField && (linkedField.IsLinked === 1 || linkedField.IsLinked === true);

    // Use datatype from MGB_Fields if available, otherwise fall back to dataType
    const fieldDataType = field.datatype || field.dataType;
    
    // Determine field input type based on datatype
    let fieldInputType = 'text';
    if (fieldDataType === 'int' || fieldDataType === 'bigint' || fieldDataType === 'smallint' || fieldDataType === 'tinyint') {
      fieldInputType = 'number';
    } else if (fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime') {
      fieldInputType = 'datetime-local';
    } else if (fieldDataType === 'date') {
      fieldInputType = 'date';
    } else if (fieldDataType === 'time') {
      fieldInputType = 'time';
    } else if (field.type === 'password') {
      fieldInputType = 'password';
    } else if (fieldDataType === 'decimal' || fieldDataType === 'numeric' || fieldDataType === 'float' || fieldDataType === 'real' || fieldDataType === 'money' || fieldDataType === 'smallmoney') {
      fieldInputType = 'number';
    } else if (fieldDataType === 'bit') {
      fieldInputType = 'checkbox';
    }
    
    // Get field dimensions from MGB_Fields - convert strings to numbers if needed
    // Helper function to safely parse numeric strings
    const parseNumeric = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined' || trimmed.toLowerCase() === 'null') return null;
        const parsed = parseFloat(trimmed);
        return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
      }
      return null;
    };
    
    // Handle both camelCase and PascalCase property names (SQL Server returns PascalCase)
    // Backend returns: maxLength (camelCase), DefaultValue (PascalCase), displayLength (camelCase)
    const displayLength = parseNumeric(field.displayLength || field.DisplayLength);
    const maxLength = parseNumeric(field.maxLength || field.MaxLength) || parseNumeric(field.schemaMaxLength);
    const height = parseNumeric(field.height || field.Height);
    const width = parseNumeric(field.width || field.Width);
    
    
    // Build style object for field dimensions
    const fieldStyle = {};
    if (width && typeof width === 'number') fieldStyle.width = `${width}px`;
    if (height && typeof height === 'number') fieldStyle.height = `${height}px`;
    // Use displayLength to set width if no explicit width is set
    // Approximate: 1 character ≈ 0.6em (varies by font, but this is a reasonable default)
    if (!width && displayLength && typeof displayLength === 'number' && displayLength > 0) {
      fieldStyle.width = `${displayLength * 0.6}em`;
      // Also set min-width to ensure it's at least visible
      fieldStyle.minWidth = `${displayLength * 0.5}em`;
    }
    
    // Handle datetime fields with getdate default - check both property name cases
    const defaultValue = field.DefaultValue || field.defaultValue || '';
    const isGetDate = defaultValue && defaultValue.toString().toLowerCase().includes('getdate');
    const shouldSetCurrentDateTime = isGetDate && (fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime' || fieldDataType === 'date' || fieldDataType === 'time');
    
    
    // Set current date/time if getdate and field is empty - use formData value if already set, otherwise compute
    let initialDateValue = formData[field.fieldName];
    if (shouldSetCurrentDateTime && modalMode === 'create' && (!formData[field.fieldName] || formData[field.fieldName] === '')) {
      const now = new Date();
      if (fieldDataType === 'date') {
        initialDateValue = now.toISOString().split('T')[0];
      } else if (fieldDataType === 'time') {
        initialDateValue = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      } else {
        // For datetime, format as datetime-local
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        initialDateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      // Update formData immediately if it's empty
      if (!formData[field.fieldName] || formData[field.fieldName] === '') {
        setFormData(prev => ({
          ...prev,
          [field.fieldName]: initialDateValue
        }));
      }
    }

    return (
      <Form.Group className="mb-3">
        <Form.Label>{getFieldDisplayName(field.fieldName)}</Form.Label>
        
        {/* Multi-Select (Cross-Table) - Checkboxes */}
        {field.type === 'multiselect' ? (
          crossTableAvailableItems[field.fieldName] ? (
            <div className="border p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
              {crossTableAvailableItems[field.fieldName].items.map((item) => {
                const config = crossTableAvailableItems[field.fieldName].config;
                
                let primaryKeyName, displayName;
                if (config.RightTable && tableName.toLowerCase() === config.RightTable.toLowerCase()) {
                  primaryKeyName = config.LeftPrimary || config.leftPrimary;
                  const tableBaseName = config.LeftTable.replace(/^MGB_/, '');
                  const singularBase = tableBaseName.replace(/s$/, '');
                  displayName = `${singularBase}Name`;
                } else {
                  primaryKeyName = config.RightPrimary || config.rightPrimary;
                  displayName = config.RightName || config.rightName;
                }
                
                const itemID = item[primaryKeyName];
                const itemName = item[displayName];
                const selectedItemsForField = (crossTableSelectedItems[field.fieldName] || []).map(s => String(s));
                const isSelected = selectedItemsForField.includes(String(itemID));
                
                return (
                  <Form.Check
                    key={`${field.fieldName}-${itemID}`}
                    type="checkbox"
                    label={itemName}
                    checked={isSelected}
                    onChange={(e) => {
                      const newSelected = e.target.checked
                        ? [...(crossTableSelectedItems[field.fieldName] || []), itemID]
                        : (crossTableSelectedItems[field.fieldName] || []).filter(id => String(id) !== String(itemID));
                      setCrossTableSelectedItems({
                        ...crossTableSelectedItems,
                        [field.fieldName]: newSelected
                      });
                    }}
                    className="mb-2"
                  />
                );
              })}
            </div>
          ) : (
            <div className="alert alert-warning" role="alert">
              Loading options...
            </div>
          )
        ) : /* Linked Field - Dropdown */
        !field.type.includes('multiselect') && isLinked && linkedFieldOptions[field.fieldName] ? (
          <Form.Select
            value={formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''}
            onChange={(e) => {
              setFormData({
                ...formData,
                [field.fieldName]: e.target.value
              });
            }}
          >
            <option value="">Select {getFieldDisplayName(field.fieldName)}</option>
            {linkedFieldOptions[field.fieldName].map(item => {
              const optionValue = tableName === 'MGB_TablePermissions' && field.fieldName === 'TableName' 
                ? item.display 
                : String(item.id);
              return (
                <option key={item.id} value={optionValue}>
                  {item.display}
                </option>
              );
            })}
          </Form.Select>
        ) : fieldDataType === 'bit' ? (
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              label="Yes"
              name={field.fieldName}
              value="1"
              checked={formData[field.fieldName] === 1 || formData[field.fieldName] === '1' || formData[field.fieldName] === true}
              onChange={(e) => setFormData({
                ...formData,
                [field.fieldName]: 1
              })}
            />
            <Form.Check
              type="radio"
              label="No"
              name={field.fieldName}
              value="0"
              checked={formData[field.fieldName] === 0 || formData[field.fieldName] === '0' || formData[field.fieldName] === false}
              onChange={(e) => setFormData({
                ...formData,
                [field.fieldName]: 0
              })}
            />
          </div>
        ) : foreignKeys.some(fk => fk.localColumn === field.fieldName) ? (
          <Form.Select
            value={formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''}
            onChange={(e) => setFormData({
              ...formData,
              [field.fieldName]: e.target.value
            })}
          >
            <option value="">Select {getFieldDisplayName(field.fieldName)}</option>
            {(foreignKeyData[field.fieldName] || []).map(item => {
              const optionValue = tableName === 'MGB_TablePermissions' && field.fieldName === 'TableName' 
                ? item.display 
                : String(item.id);
              return (
                <option key={item.id} value={optionValue}>
                  {item.display}
                </option>
              );
            })}
          </Form.Select>
        ) : (
          <>
            {height && height > 50 ? (
              // Use textarea for fields with height > 50px
              <Form.Control
                as="textarea"
                rows={Math.ceil(height / 20) || 3}
                placeholder={`Enter ${getFieldDisplayName(field.fieldName)}`}
                value={formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''}
                onChange={(e) => setFormData({
                  ...formData,
                  [field.fieldName]: e.target.value
                })}
                required={field.isNullable === 'NO'}
                maxLength={maxLength && maxLength > 0 ? Math.floor(maxLength) : undefined}
                style={Object.keys(fieldStyle).length > 0 ? fieldStyle : (width || height ? { width: width ? `${width}px` : undefined, height: height ? `${height}px` : undefined } : undefined)}
              />
            ) : (
              <Form.Control
                type={fieldInputType}
                placeholder={`Enter ${getFieldDisplayName(field.fieldName)}`}
                value={(initialDateValue !== undefined ? initialDateValue : (formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''))}
                onChange={(e) => setFormData({
                  ...formData,
                  [field.fieldName]: e.target.value
                })}
                required={field.isNullable === 'NO'}
                maxLength={maxLength && maxLength > 0 ? Math.floor(maxLength) : undefined}
                size={displayLength && displayLength > 0 ? Math.floor(displayLength) : undefined}
                style={Object.keys(fieldStyle).length > 0 ? fieldStyle : undefined}
                {...(fieldInputType === 'number' && fieldDataType !== 'int' && fieldDataType !== 'bigint' ? { step: 'any' } : {})}
              />
            )}
            {/* Password Confirmation Field */}
            {field.type === 'password' && (
              <Form.Control
                type="password"
                placeholder={`Confirm ${getFieldDisplayName(field.fieldName)}`}
                value={formData[`${field.fieldName}_confirm`] !== undefined && formData[`${field.fieldName}_confirm`] !== null ? String(formData[`${field.fieldName}_confirm`]) : ''}
                onChange={(e) => setFormData({
                  ...formData,
                  [`${field.fieldName}_confirm`]: e.target.value
                })}
                className="mt-2"
                required={field.isNullable === 'NO'}
              />
            )}
          </>
        )}
      </Form.Group>
    );
  };


  const handleDelete = async (record) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        // Get the primary key column and value
        let pkColumn = primaryKeyFields && primaryKeyFields.length > 0 ? primaryKeyFields[0] : 'ID';
        let pkValue = record[pkColumn] || Object.values(record)[0];
        
        const response = await recordAPI.deleteRecord(tableName, pkValue);
        if (response.data.success) {
          fetchRecords();
          setError('');
        } else {
          setError(response.data.message || 'Failed to delete record');
        }
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message || 'Failed to delete record';
        setError(errorMsg);

      }
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1);
    fetchRecords();
  };

  const handleKeywordSearch = (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1);
    fetchRecords();
  };

  const handleFilterChange = (fieldName, value, isImmediate = false) => {
    if (isImmediate) {
      // For dropdowns - update immediately
      setImmediateFieldFilters(prev => {
        const newFilters = { ...prev };
        if (value === null || value === undefined || value === '') {
          delete newFilters[fieldName];
        } else {
          newFilters[fieldName] = value;
        }
        // Also remove from debounced filters if it exists there
        setDebouncedFieldFilters(prev => {
          const newDebounced = { ...prev };
          delete newDebounced[fieldName];
          return newDebounced;
        });
        return newFilters;
      });
    } else {
      // For text inputs - use debounced approach
      setFieldFilters(prev => {
        const newFilters = { ...prev };
        if (value === null || value === undefined || value === '') {
          delete newFilters[fieldName];
        } else {
          newFilters[fieldName] = value;
        }
        return newFilters;
      });
      // Remove from immediate filters if it exists there
      setImmediateFieldFilters(prev => {
        const newImmediate = { ...prev };
        delete newImmediate[fieldName];
        return newImmediate;
      });
    }
    setCurrentPage(1);
  };

  const handleDateRangeChange = (fieldName, dateType, value, isImmediate = true) => {
    // dateType: 'from' or 'to'
    setImmediateFieldFilters(prev => {
      const newFilters = { ...prev };
      const currentRange = newFilters[fieldName] || {};
      
      if (value === null || value === undefined || value === '') {
        delete currentRange[dateType];
      } else {
        currentRange[dateType] = value;
      }
      
      // If both from and to are empty, remove the filter entirely
      if (!currentRange.from && !currentRange.to) {
        delete newFilters[fieldName];
      } else {
        newFilters[fieldName] = currentRange;
      }
      
      // Also remove from debounced filters if it exists there
      setDebouncedFieldFilters(prev => {
        const newDebounced = { ...prev };
        delete newDebounced[fieldName];
        return newDebounced;
      });
      
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setKeywordSearch('');
    setFieldFilters({});
    setImmediateFieldFilters({});
    setDebouncedFieldFilters({});
    setSearchQuery('');
    setSearchField('');
    setCurrentPage(1);
  };

  const convertCamelCaseToSpaced = (str) => {
    // Convert camelCase to spaced words
    // e.g., firstName -> first Name, userEmail -> user Email
    return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (char) => char.toUpperCase());
  };

  const getFieldDisplayName = (fieldName) => {
    // If displayName exists, use it
    if (displayNames[fieldName]) {
      return displayNames[fieldName];
    }
    // If no displayName and field name is camelCase, convert it
    if (/[a-z][A-Z]/.test(fieldName)) {
      return convertCamelCaseToSpaced(fieldName);
    }
    // Otherwise return the field name as is
    return fieldName;
  };

  const formatFieldValue = (fieldName, value) => {
    if (value === null || value === undefined) return '';
    
    // Find the field definition to check its data type
    const field = fields.find(f => f.fieldName === fieldName);
    if (!field) return value;

    // Debug: show linked-field resolution attempt
    const linkedField = fieldLinks.find(fl => 
      fl.FieldName && fl.FieldName.toLowerCase() === fieldName.toLowerCase()
    );
    const isLinked = linkedField && (linkedField.IsLinked === 1 || linkedField.IsLinked === true);
    if (isLinked) {
      const optionsForField = linkedFieldOptions[fieldName] || linkedFieldOptions[fieldName.toLowerCase()];
      if (optionsForField && Array.isArray(optionsForField)) {
        const matchedOption = optionsForField.find(
          item => String(item.id) === String(value)
        );
        if (matchedOption) {
          return matchedOption.display;
        }
      }
    }

    // Format BIT fields as Yes/No
    if (field.dataType === 'bit') {
      return value === 1 || value === true || value === '1' ? 'Yes' : 'No';
    }

    // Format DateTime fields
    if (field.dataType === 'datetime' || field.dataType === 'datetime2') {
      try {
        const date = new Date(value);
        return date.toLocaleString();
      } catch {
        return value;
      }
    }

    // Format Date fields
    if (field.dataType === 'date') {
      try {
        const date = new Date(value);
        return date.toLocaleDateString();
      } catch {
        return value;
      }
    }

    return value;
  };

  const handleExportXLSX = () => {
    try {
      if (searchQuery && searchField) {
        // Export filtered results
        exportFilteredRecords(
          records,
          fields,
          displayNames,
          tableName,
          linkedFieldOptions,
          fieldLinks,
          { [searchField]: searchQuery }
        );
      } else {
        // Export all visible records on current page
        exportToXLSX(
          records,
          fields,
          displayNames,
          tableName,
          linkedFieldOptions,
          fieldLinks
        );
      }
    } catch (err) {
      setError('Failed to export data: ' + err.message);
    }
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <Container fluid className="table-viewer">
      <div className="mb-3">
        <h2 className="table-title text-center" style={{ marginTop: 0, marginBottom: '10px', color: '#333' }}>{tableName}</h2>
        <div className="d-flex justify-content-end">
          <div className="header-buttons d-flex gap-2">
            {isTableRegistered && (
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowFieldConfig(true)}
                className="btn-config"
                size="sm"
                style={{ padding: '0.2rem 0.4rem', fontSize: '11px', whiteSpace: 'nowrap' }}
              >
                <FiSettings style={{ fontSize: '11px', marginRight: '4px' }} />
                Configure
              </Button>
            )}
            {tableMetadata.AddnewLinkfromTable && (permissions.CanCreate || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
              <Button 
                variant="success" 
                onClick={handleAddClick}
                className="btn-add"
                size="sm"
                style={{ padding: '0.2rem 0.4rem', fontSize: '11px', whiteSpace: 'nowrap' }}
              >
                <FiPlus style={{ fontSize: '11px', marginRight: '4px' }} />
                Add Record
              </Button>
            )}
            {permissions.CanExport || isAdmin ? (
              <Button 
                variant="info" 
                onClick={handleExportXLSX}
                className="btn-export"
                title="Export current data to Excel"
                size="sm"
                style={{ padding: '0.2rem 0.4rem', fontSize: '11px' }}
              >
                <FiDownload style={{ fontSize: '11px', marginRight: '4px' }} />
                Export
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <FieldConfigPanel 
        show={showFieldConfig} 
        onHide={() => setShowFieldConfig(false)} 
        tableName={tableName} 
        fields={fields} 
        onSave={async (settings) => {
          // Reload fields from API to get the updated visibility settings
          try {
            const fieldsRes = await tableAPI.getTableFields(tableName);
            if (fieldsRes.data.success) {
              setFields(fieldsRes.data.fields);
              
              // Update display names
              const displayNamesMap = {};
              fieldsRes.data.fields.forEach(field => {
                displayNamesMap[field.fieldName] = field.displayName || field.fieldName;
              });
              setDisplayNames(displayNamesMap);
              
              // Filter visible fields based on the fresh data (shortViewable = 1 or true)
              const visible = fieldsRes.data.fields.filter(f => {
                const isVisible = f.shortViewable === 1 || f.shortViewable === true;

                return isVisible;
              });

              setVisibleFields(visible);
              

            }
          } catch (err) {

          }
        }} 
      />

      {(() => {
        const searchableFields = fields.filter(field => field.searchable === true || field.searchable === 1);
        const hasSearchableFields = searchableFields.length > 0;
        
        return (
          <Row className="g-3">
            {/* Main Content Area - Left Side */}
            <Col lg={hasSearchableFields ? 9 : 12}>
          {/* Keyword Search */}
          <div className="search-section mb-3">
            <Form onSubmit={handleKeywordSearch}>
              <InputGroup size="sm" style={{ maxWidth: '500px' }}>
                <InputGroup.Text style={{ 
                  backgroundColor: '#e9ecef', 
                  borderColor: '#dee2e6'
                }}>
                  <FiSearch style={{ color: '#495057' }} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Keyword Search..."
                  value={keywordSearch}
                  onChange={(e) => setKeywordSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleKeywordSearch(e);
                    }
                  }}
                  style={{ 
                    fontSize: '13px',
                    borderColor: '#dee2e6'
                  }}
                />
                <Button 
                  onClick={handleKeywordSearch} 
                  size="sm"
                  type="button"
                  variant="outline-secondary"
                  style={{ 
                    color: '#495057',
                    fontWeight: '500',
                    background: 'linear-gradient(to bottom, #f8f9fa, #e9ecef)',
                    borderColor: '#dee2e6',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(to bottom, #e9ecef, #dee2e6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(to bottom, #f8f9fa, #e9ecef)';
                  }}
                >
                  Search
                </Button>
              </InputGroup>
              <Form.Text className="text-muted" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                Search across all searchable fields
              </Form.Text>
            </Form>
          </div>

          {/* Records Display */}
          {loading ? (
            <p>Loading records...</p>
          ) : (
            <>
              <div className="record-count mb-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  <strong>{totalRecords}</strong> {totalRecords === 1 ? 'record' : 'records'} found
                  {totalPages > 1 && ` - Page ${currentPage} of ${totalPages}`}
                </p>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '14px' }}>Show</span>
                  <Form.Select 
                    value={pageSize} 
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: 'auto', display: 'inline-block' }}
                    size="sm"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                  <span className="text-muted" style={{ fontSize: '14px' }}>records per page</span>
                </div>
              </div>

          {(() => {
            // Get search result format
            const searchResultFormat = formats.searchResultFormat;
            
            let formatConfig = null;
            try {
              if (searchResultFormat && searchResultFormat.FormatConfig) {
                formatConfig = typeof searchResultFormat.FormatConfig === 'string' 
                  ? JSON.parse(searchResultFormat.FormatConfig) 
                  : searchResultFormat.FormatConfig;
              }
            } catch (e) {
              // Format config parsing failed, use defaults
            }
            const displayMode = formatConfig && formatConfig.displayMode ? formatConfig.displayMode : 'table';
            
            // Use visible fields, or fallback to first 5 non-password fields if none are configured
            const displayFields = visibleFields.length > 0 ? visibleFields : fields.filter(f => f.type !== 'password').slice(0, 5);
            
            // Render based on display mode
            if (displayMode === 'card' && formatConfig && formatConfig.columns) {
              // Card Grid Layout
              const columns = formatConfig.columns || 3;
              return (
                <div className="row g-3">
                  {records.length > 0 ? records.map((record, idx) => (
                    <div key={idx} className={`col-md-${12 / columns}`}>
                      <div className="card h-100">
                        <div className="card-body">
                          {displayFields.map(field => (
                            <div key={field.fieldName} className="mb-2">
                              <strong>{getFieldDisplayName(field.fieldName)}:</strong> {formatFieldValue(field.fieldName, record[field.fieldName])}
                            </div>
                          ))}
                          <div className="mt-3 d-flex gap-2">
                            {tableMetadata.LongViewLinkfromTable && (permissions.CanView || isAdmin) && (
                              <Button variant="info" size="sm" onClick={() => { setSelectedRecord(record); setShowDetailModal(true); }}>
                                <FiEye /> View
                              </Button>
                            )}
                            {tableMetadata.EditViewLinkfromTable && (permissions.CanEdit || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
                              <Button variant="warning" size="sm" onClick={() => handleEditClick(record)}>
                                <FiEdit2 /> Edit
                              </Button>
                            )}
                            {tableMetadata.DeletefromTableLink && (permissions.CanDelete || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
                              <Button variant="danger" size="sm" onClick={() => handleDelete(record)}>
                                <FiTrash2 /> Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="col-12">
                      <div className="alert alert-info">No records found</div>
                    </div>
                  )}
                </div>
              );
            } else if (displayMode === 'list') {
              // List Layout
              return (
                <div className="list-container">
                  {records.length > 0 ? records.map((record, idx) => (
                    <div key={idx} className="list-item border rounded p-3 mb-3">
                      <Row>
                        {displayFields.map(field => (
                          <Col key={field.fieldName} md={formatConfig && formatConfig.showDetails ? 6 : 12}>
                            <strong>{getFieldDisplayName(field.fieldName)}:</strong> {formatFieldValue(field.fieldName, record[field.fieldName])}
                          </Col>
                        ))}
                        <Col md={12} className="mt-2">
                          <div className="d-flex gap-2">
                            {tableMetadata.LongViewLinkfromTable && (permissions.CanView || isAdmin) && (
                              <Button variant="info" size="sm" onClick={() => { setSelectedRecord(record); setShowDetailModal(true); }}>
                                <FiEye /> View
                              </Button>
                            )}
                            {tableMetadata.EditViewLinkfromTable && (permissions.CanEdit || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
                              <Button variant="warning" size="sm" onClick={() => handleEditClick(record)}>
                                <FiEdit2 /> Edit
                              </Button>
                            )}
                            {tableMetadata.DeletefromTableLink && (permissions.CanDelete || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
                              <Button variant="danger" size="sm" onClick={() => handleDelete(record)}>
                                <FiTrash2 /> Delete
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )) : (
                    <div className="alert alert-info">No records found</div>
                  )}
                </div>
              );
            } else {
              // Table Layout (default)
              return (
                <div className="table-container">
                  <table className="records-table">
                    <thead>
                      <tr>
                        {displayFields.length > 0 ? displayFields.map(field => (
                          <th key={field.fieldName}>{getFieldDisplayName(field.fieldName)}</th>
                        )) : null}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.length > 0 ? records.map((record, idx) => (
                        <tr key={idx}>
                          {displayFields.map(field => (
                            <td key={field.fieldName}>{formatFieldValue(field.fieldName, record[field.fieldName])}</td>
                          ))}
                          <td>
                            <div className="action-buttons">
                              {tableMetadata.LongViewLinkfromTable && (permissions.CanView || isAdmin) && (
                                <Button 
                                  variant="info" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRecord(record);
                                    setShowDetailModal(true);
                                  }}
                                  title="View record details"
                                >
                                  <FiEye />
                                </Button>
                              )}
                              {tableMetadata.EditViewLinkfromTable && (permissions.CanEdit || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
                                <Button 
                                  variant="warning" 
                                  size="sm"
                                  onClick={() => handleEditClick(record)}
                                >
                                  <FiEdit2 />
                                </Button>
                              )}
                              {tableMetadata.DeletefromTableLink && (permissions.CanDelete || isAdmin) && (!PROTECTED_SYSTEM_TABLES.includes(tableName) || isAdmin) && (
                                <Button 
                                  variant="danger" 
                                  size="sm"
                                  onClick={() => handleDelete(record)}
                                >
                                  <FiTrash2 />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="100%" style={{ textAlign: 'center', padding: '20px' }}>No records found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            }
          })()}

          {/* Pagination */}
          {totalPages > 0 && (
            <Pagination className="mt-3">
              <Pagination.First 
                disabled={currentPage === 1 || totalPages === 0}
                onClick={() => setCurrentPage(1)}
              />
              <Pagination.Prev 
                disabled={currentPage === 1 || totalPages === 0}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next 
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
              <Pagination.Last 
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(totalPages)}
              />
            </Pagination>
          )}
            </>
          )}
            </Col>

            {/* Sidebar Filters - Right Side */}
            {hasSearchableFields && (
              <Col lg={3}>
                <div className="filter-sidebar" style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '20px', 
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  position: 'sticky',
                  top: '20px',
                  maxHeight: 'calc(100vh - 40px)',
                  overflowY: 'auto'
                }}>
                  <div style={{ 
                    backgroundColor: '#6c757d', 
                    color: 'white', 
                    padding: '12px 16px', 
                    margin: '-20px -20px 20px -20px',
                    borderRadius: '4px 4px 0 0',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    FILTER
                  </div>
                  
                  <>
                      {searchableFields.map(field => {
                        const fieldName = field.fieldName;
                        const isLinked = fieldLinks.some(fl => 
                          fl.FieldName && fl.FieldName.toLowerCase() === fieldName.toLowerCase() && 
                          (fl.IsLinked === 1 || fl.IsLinked === true)
                        );
                        const isForeignKey = foreignKeys.some(fk => fk.localColumn === fieldName);
                        const fieldDataType = field.datatype || field.dataType;
                        const isDateField = fieldDataType === 'date' || fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime';
                        const dateRangeFilter = combinedFilters[fieldName] && typeof combinedFilters[fieldName] === 'object' ? combinedFilters[fieldName] : {};
                        
                        return (
                          <Form.Group key={fieldName} className="mb-3">
                            <Form.Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                              By {getFieldDisplayName(fieldName)}
                            </Form.Label>
                            
                            {isDateField ? (
                              <>
                                <Form.Label style={{ fontSize: '12px', fontWeight: '400', marginBottom: '4px', color: '#6c757d' }}>
                                  From date
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  value={dateRangeFilter.from || ''}
                                  onChange={(e) => handleDateRangeChange(fieldName, 'from', e.target.value)}
                                  size="sm"
                                  style={{ marginBottom: '8px' }}
                                />
                                <Form.Label style={{ fontSize: '12px', fontWeight: '400', marginBottom: '4px', color: '#6c757d' }}>
                                  To date
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  value={dateRangeFilter.to || ''}
                                  onChange={(e) => handleDateRangeChange(fieldName, 'to', e.target.value)}
                                  size="sm"
                                />
                              </>
                            ) : isLinked && linkedFieldOptions[fieldName] ? (
                              <Form.Select
                                value={combinedFilters[fieldName] || ''}
                                onChange={(e) => handleFilterChange(fieldName, e.target.value, true)}
                                size="sm"
                              >
                                <option value="">All</option>
                                {linkedFieldOptions[fieldName].map(item => (
                                  <option key={item.id} value={item.display}>
                                    {item.display}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : isForeignKey && foreignKeyData[fieldName] ? (
                              <Form.Select
                                value={combinedFilters[fieldName] || ''}
                                onChange={(e) => handleFilterChange(fieldName, e.target.value, true)}
                                size="sm"
                              >
                                <option value="">All</option>
                                {foreignKeyData[fieldName].map(item => (
                                  <option key={item.id} value={item.display}>
                                    {item.display}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : fieldDataType === 'bit' ? (
                              <Form.Select
                                value={combinedFilters[fieldName] || ''}
                                onChange={(e) => handleFilterChange(fieldName, e.target.value, true)}
                                size="sm"
                              >
                                <option value="">All</option>
                                <option value="1">Yes</option>
                                <option value="0">No</option>
                              </Form.Select>
                            ) : (
                              <Form.Control
                                type="text"
                                placeholder={`Filter by ${getFieldDisplayName(fieldName)}...`}
                                value={fieldFilters[fieldName] || ''}
                                onChange={(e) => handleFilterChange(fieldName, e.target.value, false)}
                                size="sm"
                              />
                            )}
                          </Form.Group>
                        );
                      })}
                      
                      {(keywordSearch || Object.keys(combinedFilters).some(key => combinedFilters[key] !== null && combinedFilters[key] !== undefined && combinedFilters[key] !== '')) && (
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={handleClearFilters}
                          className="w-100 mt-2"
                        >
                          Clear All Filters
                        </Button>
                      )}
                    </>
                </div>
              </Col>
            )}
          </Row>
        );
      })()}

      {/* Record Detail Modal */}
      <RecordDetailModal 
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        record={selectedRecord}
        fields={fields}
        tableName={tableName}
        getFieldDisplayName={getFieldDisplayName}
        formatFieldValue={formatFieldValue}
      />
    </Container>
  );
}

export default TableViewer;
